<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AdminArea from './components/admin/AdminArea.vue';
import ShowsSection from './components/ShowsSection.vue';
import MusicSection from './components/MusicSection.vue';
import PhotosSection from './components/PhotosSection.vue';
import LanguageSwitcher from './components/LanguageSwitcher.vue';
import ImprintPage from './components/ImprintPage.vue';
import PrivacyPage from './components/PrivacyPage.vue';
import MaintenancePage from './components/MaintenancePage.vue';
import VideoGallery from './components/VideoGallery.vue'
import CookieBanner from './components/CookieBanner.vue'
import ScrollToTop from './components/ScrollToTop.vue';
import FanPhotoUpload from './components/FanPhotoUpload.vue';
import FanPhotoGallery from './components/FanPhotoGallery.vue';
import FanPhotoQRCode from './components/FanPhotoQRCode.vue';
import AdminFanPhotos from './components/AdminFanPhotos.vue';

const { t } = useI18n();

// Einfaches Routing
const currentRoute = ref(window.location.pathname);

// Ref für CookieBanner-Komponente
const cookieBannerRef = ref(null);

// Cookie-Einstellungen öffnen
const openCookieSettings = () => {
  if (cookieBannerRef.value) {
    cookieBannerRef.value.reopenBanner();
  }
};

// Prüfen, ob wir auf einer Unterseite sind
const isSubpage = computed(() => {
  return ['/impressum', '/datenschutz'].includes(currentRoute.value);
});

// Komponenten-Anzeige basierend auf Route
const showImprint = computed(() => currentRoute.value === '/impressum');
const showPrivacy = computed(() => currentRoute.value === '/datenschutz');
const showFanUpload = computed(() => currentRoute.value === '/fan-upload' && (siteSettings.value.show_fan_section === true || isAdmin.value));
const showFanGallery = computed(() => currentRoute.value === '/fan-galerie' && (siteSettings.value.show_fan_section === true || isAdmin.value));
const showFanQrCode = computed(() => currentRoute.value === '/fan-qrcode' && (siteSettings.value.show_fan_section === true || isAdmin.value));

// Shows-Daten
const shows = ref([]);

// Shows laden
const loadShows = async () => {
  try {
    const response = await fetch('/api/shows.php');
    const data = await response.json();
    if (data.success) {
      shows.value = data.shows.map(show => ({
        ...show,
        date: new Date(show.date).toISOString().split('T')[0] // Formatiere das Datum korrekt
      }));
    } else {
      console.error('Fehler beim Laden der Shows:', data.error);
    }
  } catch (err) {
    console.error('Fehler beim Laden der Shows:', err);
  }
};

// Besuchertracking-Funktion
const trackPageView = async () => {
  // Admin-Bereich nicht tracken
  if (currentRoute.value.includes('/admin') || isAdminRoute.value) {
    return;
  }
  
  try {
    await fetch('/api/track_visitor.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        page: currentRoute.value || '/'
      })
    });
  } catch (error) {
    console.error('Fehler beim Tracking:', error);
  }
};

// Gruppierte Shows nach Monaten
const groupedShows = computed(() => {
  const months = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];
  
  const groups = {};
  shows.value.forEach(show => {
    const month = new Date(show.date).getMonth();
    if (!groups[month]) {
      groups[month] = {
        monthName: months[month],
        shows: []
      };
    }
    groups[month].shows.push(show);
  });
  
  return Object.values(groups).sort((a, b) => {
    const monthA = months.indexOf(a.monthName);
    const monthB = months.indexOf(b.monthName);
    return monthA - monthB;
  });
});

// Verwaltung der offenen Monate
const openMonths = ref({});

const toggleMonth = (index) => {
  openMonths.value[index] = !openMonths.value[index];
};

// Öffne den aktuellen Monat automatisch
onMounted(() => {
  loadShows();
  const currentMonth = new Date().getMonth();
  openMonths.value[currentMonth] = true;
  
  // Beim ersten Laden der Seite Besuch tracken
  trackPageView();
  
  // Auf Routenänderungen hören
  window.addEventListener('popstate', () => {
    currentRoute.value = window.location.pathname;
  });
});

// Bei Routenänderungen Tracking durchführen
watch(currentRoute, () => {
  trackPageView();
});

// Komponentenimporte hier

// Wartungsmodus Status
const maintenanceMode = ref(false);
const siteSettings = ref({
  maintenance_mode: false,
  show_fan_section: true,
  site_title: 'HomeWard - Irish Folk Band',
  contact_email: '',
  footer_text: '© HomeWard Irish Folk Band',
  facebook_url: '',
  youtube_url: '',
  tiktok_url: '',
  hero_image: '/upload/homeward.jpg', // Standard-Bild
  calendar_image: '/upload/homeward-2023-onlinekalender.jpg', // Standard-Kalenderbild
  contact_phone: '+49 123 456 7890',
  contact_location: 'München, Deutschland'
});

// Prüfen, ob der Benutzer als Admin angemeldet ist
const isAdmin = ref(false);
const isLoading = ref(true);

// About-Inhalte
const aboutContent = ref({
  band_description: '',
  band_image: '',
  members: []
});

// Hero-Bild-URL berechnen mit Fallback auf Standard-Bild
const heroBackgroundImage = computed(() => {
  const imagePath = siteSettings.value.hero_image || '/upload/homeward.jpg';
  return `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('${imagePath}')`;
});

// Einstellungen laden
const loadSiteSettings = async () => {
  try {
    const response = await fetch('/api/settings.php');
    const data = await response.json();
    
    if (data.success && data.settings) {
      siteSettings.value = {
        ...siteSettings.value,
        ...data.settings,
        maintenance_mode: data.settings.maintenance_mode === '1',
        show_fan_section: data.settings.show_fan_section === '1'
      };
      
      // Wartungsmodus-Status aktualisieren
      maintenanceMode.value = siteSettings.value.maintenance_mode;
      console.log('Wartungsmodus Status:', maintenanceMode.value);
    }
  } catch (err) {
    console.error('Fehler beim Laden der Seiteneinstellungen:', err);
  } finally {
    isLoading.value = false;
  }
};

// Prüfen, ob der Benutzer Admin ist
const checkAdminStatus = async () => {
  try {
    console.log('Prüfe Admin-Status...');
    const response = await fetch('/api/check_admin.php', {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      credentials: 'include'
    });
    
    const data = await response.json();
    console.log('API Antwort:', data);
    
    if (data.success && data.authenticated) {
      console.log('Admin-Status: Authentifiziert');
      isAdmin.value = true;
    } else {
      console.log('Admin-Status: Nicht authentifiziert');
      isAdmin.value = false;
      localStorage.removeItem('adminToken');
      localStorage.removeItem('admin_expires');
    }
  } catch (error) {
    console.error('Fehler beim Prüfen des Admin-Status:', error);
    isAdmin.value = false;
    localStorage.removeItem('adminToken');
    localStorage.removeItem('admin_expires');
  } finally {
    isLoading.value = false;
  }
};

// Login-Handler
const handleLogin = async (credentials) => {
  try {
    console.log('Sende Login-Anfrage...');
    const response = await fetch('/api/auth.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(credentials)
    });
    
    const data = await response.json();
    console.log('Login-Antwort:', data);
    
    if (data.success && data.token) {
      console.log('Login erfolgreich');
      isAdmin.value = true;
      return true;
    } else {
      console.error('Login fehlgeschlagen:', data.error);
      return false;
    }
  } catch (error) {
    console.error('Fehler beim Login:', error);
    return false;
  }
};

// About-Inhalte laden
const loadAboutContent = async () => {
  try {
    const response = await fetch('/api/about.php');
    const data = await response.json();
    
    if (data.success && data.about) {
      aboutContent.value = data.about;
    }
  } catch (err) {
    console.error('Fehler beim Laden der About-Inhalte:', err);
  } finally {
    isLoading.value = false;
  }
};

// Prüfen, ob wir uns im Admin-Bereich befinden
const isAdminRoute = computed(() => {
  return currentRoute.value.includes('/admin');
});

// Prüfen, ob ein Admin-Bypass in der URL existiert
const hasAdminBypass = computed(() => {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.has('bypass');
});

// Prüfen, ob der Wartungsmodus angezeigt werden soll
const showMaintenanceMode = computed(() => {
  console.log('Prüfe Wartungsmodus-Anzeige:');
  console.log('- Wartungsmodus aktiv:', maintenanceMode.value);
  console.log('- Admin-Route:', isAdminRoute.value);
  console.log('- Admin eingeloggt:', isAdmin.value);
  console.log('- Admin-Bypass:', hasAdminBypass.value);
  
  // Wenn der Wartungsmodus nicht aktiv ist, nichts anzeigen
  if (!maintenanceMode.value) {
    console.log('Wartungsmodus ist nicht aktiv');
    return false;
  }
  
  // Wartungsmodus nie anzeigen, wenn wir im Admin-Bereich sind
  if (isAdminRoute.value) {
    console.log('Admin-Bereich - Wartungsmodus wird nicht angezeigt');
    return false;
  }
  
  // Wartungsmodus nie anzeigen, wenn Admin eingeloggt ist oder Admin-Bypass aktiv ist
  if (isAdmin.value || hasAdminBypass.value) {
    console.log('Admin eingeloggt oder Bypass aktiv - Wartungsmodus wird nicht angezeigt');
    return false;
  }
  
  // Wenn wir hier ankommen, soll der Wartungsmodus angezeigt werden
  console.log('Wartungsmodus wird angezeigt');
  return true;
});

// Prüfen ob wir das Admin-Banner anzeigen sollen
const showAdminBanner = computed(() => {
  return maintenanceMode.value && (isAdmin.value || hasAdminBypass.value);
});

// Mobiles Menü
const mobileMenuOpen = ref(false);

// Toggle Mobile-Menü
const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
};

// Schließe Mobile-Menü bei Klick auf einen Link
const closeMenu = () => {
  mobileMenuOpen.value = false;
};

// Prüfen, ob das Token abgelaufen ist
const checkTokenExpiration = () => {
  const expiration = localStorage.getItem('admin_expires');
  
  if (expiration) {
    const expirationTime = parseInt(expiration, 10);
    const now = Math.floor(Date.now() / 1000); // Aktuelle Zeit in Sekunden
    
    // Wenn das Token abgelaufen ist oder weniger als 10 Minuten gültig
    if (expirationTime <= now || expirationTime - now < 600) {
      console.log("Admin-Token ist abgelaufen, entferne Authentifizierungsdaten");
      localStorage.removeItem('admin_authenticated');
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_expires');
      
      // Setze den Admin-Status zurück
      isAdmin.value = false;
    }
  }
};

// Schließe Mobile-Menü beim Scrollen
onMounted(async () => {
  isLoading.value = true;
  try {
    // Einstellungen und About-Inhalte parallel laden
    await Promise.all([
      loadSiteSettings(),
      loadAboutContent(),
      checkAdminStatus()
    ]);
  } catch (error) {
    console.error('Fehler beim Laden der Daten:', error);
  } finally {
    isLoading.value = false;
  }
  
  // Event-Listener für Popstate-Events (Browser-Navigation)
  window.addEventListener('popstate', () => {
    currentRoute.value = window.location.pathname;
  });
  
  // Event-Listener für Scrollen
  window.addEventListener('scroll', () => {
    if (mobileMenuOpen.value) {
      mobileMenuOpen.value = false;
    }
  });
});

// Interner Link-Handler
const handleInternalLink = (e, path) => {
  // Nur behandeln, wenn es ein interner Link ist
  if (path.startsWith('/')) {
    e.preventDefault();
    // URL im Browser aktualisieren
    window.history.pushState(null, '', path);
    // Route aktualisieren
    currentRoute.value = path;
    console.log('Route aktualisiert:', currentRoute.value);
    // Zum Seitenanfang scrollen
    window.scrollTo(0, 0);
  }
};

// Formatierte Bandbeschreibung mit HTML-Entities
const formattedBandDescription = computed(() => {
  if (!aboutContent.value.band_description) return 'Willkommen bei HomeWard, Ihrer irischen Folk-Band.';
  
  // HTML-Entities dekodieren
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = aboutContent.value.band_description;
  return tempDiv.textContent;
});

// Social-Media-Links in neuen Tabs öffnen
const openSocialLink = (url) => {
  if (url) {
    window.open(url, '_blank');
  }
};

// Verwende normale JavaScript-Objekte anstatt reaktiver Refs für formData
const formData = {
  name: '',
  email: '',
  message: ''
};

const isSubmitting = ref(false);
const errorMessage = ref('');
const successMessage = ref('');

const handleSubmit = async (e) => {
  e.preventDefault();
  isSubmitting.value = true;
  errorMessage.value = '';
  successMessage.value = '';

  try {
    // Sammle Formulardaten direkt aus den Elementen
    const form = e.target;
    const name = form.querySelector('#name').value;
    const email = form.querySelector('#email').value;
    const message = form.querySelector('#message').value;
    
    const formDataToSend = { name, email, message };
    
    console.log('Sende Formular...');
    console.log('Formulardaten zum Senden:', formDataToSend);
    
    const response = await fetch('/api/contact.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(formDataToSend)
    });

    console.log('Antwort-Status:', response.status);
    
    const data = await response.json();
    console.log('Antwort-Daten:', data);

    if (response.ok && data.success) {
      successMessage.value = data.message || 'Ihre Nachricht wurde erfolgreich gesendet!';
      
      // Formularfelder zurücksetzen
      form.reset();
      
      // Nach 3 Sekunden die Erfolgsmeldung ausblenden
      setTimeout(() => {
        successMessage.value = '';
      }, 3000);
    } else {
      errorMessage.value = data.error || 'Ein Fehler ist aufgetreten';
      console.error('Formularsendung fehlgeschlagen:', data.error);
    }
  } catch (error) {
    console.error('Fehler beim Senden des Formulars:', error);
    errorMessage.value = 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.';
  } finally {
    isSubmitting.value = false;
  }
};

// Logout-Funktion
const handleLogout = async () => {
  try {
    const response = await fetch('/api/logout.php', {
      method: 'POST',
      credentials: 'include'
    });
    
    const data = await response.json();
    
    if (data.success) {
      // Lokalen Zustand zurücksetzen
      isAdmin.value = false;
      hasAdminBypass.value = false;
      
      // Lokale Speicherung löschen
      localStorage.removeItem('adminToken');
      localStorage.removeItem('admin_expires');
      
      // Zur Startseite weiterleiten
      window.location.href = '/';
    } else {
      console.error('Logout fehlgeschlagen:', data.error);
    }
  } catch (error) {
    console.error('Logout Fehler:', error);
  }
};

// Prüft, ob der Fan-Bereich-Link im Header angezeigt werden soll
const shouldShowFanSection = computed(() => {
  return siteSettings.value.show_fan_section === true || isAdmin.value;
});
</script>

<template>
  <div class="app">
    <div v-if="isLoading" class="loading">
      Lade...
    </div>
    <template v-else>
      <AdminArea v-if="isAdminRoute" />
      <MaintenancePage v-else-if="showMaintenanceMode" />
      
      <!-- Impressum und Datenschutz mit Header und Footer -->
      <template v-else-if="showImprint || showPrivacy || showFanGallery || showFanUpload || showFanQrCode">
        <div class="noise-overlay"></div>
        
        <!-- Admin Wartungsmodus Banner -->
        <div v-if="showAdminBanner" class="admin-maintenance-notice">
          <div class="notice-content">
            <span class="warning-icon">⚠️</span>
            <span>Wartungsmodus ist aktiviert. Die Website ist für normale Besucher nicht sichtbar.</span>
            <a href="/admin" class="admin-link-btn">Admin-Bereich</a>
          </div>
        </div>
        
        <header class="header" :class="{ 'with-notice': showAdminBanner }">
          <div class="container">
            <nav class="nav">
              <a href="/" class="logo" @click="handleInternalLink($event, '/')">
                <div class="logo-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12,2C9.1,2,6.8,4.3,6.8,7.2c0,1.7,0.8,3.3,2.1,4.3c-1.3,1-2.1,2.5-2.1,4.3c0,1.7,0.8,3.3,2.1,4.3C7.6,21,6.8,22.5,6.8,24h2.4c0-1.3,1.1-2.4,2.4-2.4c1.3,0,2.4,1.1,2.4,2.4h2.4c0-1.5-0.8-3-2.1-3.9c1.3-1,2.1-2.5,2.1-4.3c0-1.7-0.8-3.3-2.1-4.3c1.3-1,2.1-2.5,2.1-4.3C16.8,4.3,14.9,2,12,2z" fill="currentColor"/>
                  </svg>
                </div>
                HomeWard
              </a>
              
              <div class="nav-links">
                <a href="/" class="nav-link" @click="handleInternalLink($event, '/')">Home</a>
                <a href="/#about" class="nav-link">Über uns</a>
                <a href="/#shows" class="nav-link">Shows</a>
                <a href="/#music" class="nav-link">Musik</a>
                <a href="/#photos" class="nav-link">Fotos</a>
                <a href="/#videos" class="nav-link">Videos</a>
                <a href="/#contact" class="nav-link">Kontakt</a>
                <a v-if="shouldShowFanSection" href="/fan-galerie" class="nav-link" @click="handleInternalLink($event, '/fan-galerie')">{{ $t('header.fanZone') }}</a>
              </div>
              
              <div class="header-social-links">
                <a v-if="siteSettings.facebook_url" :href="siteSettings.facebook_url" target="_blank" class="header-social-link" @click="openSocialLink(siteSettings.facebook_url)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                  </svg>
                </a>
                
                <a v-if="siteSettings.youtube_url" :href="siteSettings.youtube_url" target="_blank" class="header-social-link" @click="openSocialLink(siteSettings.youtube_url)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                  </svg>
                </a>
                
                <a v-if="siteSettings.tiktok_url" :href="siteSettings.tiktok_url" target="_blank" class="header-social-link" @click="openSocialLink(siteSettings.tiktok_url)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
                
                <a v-if="siteSettings.instagram_url" :href="siteSettings.instagram_url" target="_blank" class="header-social-link" @click="openSocialLink(siteSettings.instagram_url)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                </a>
              </div>
              
              <button class="mobile-toggle" @click="toggleMobileMenu" :class="{ 'active': mobileMenuOpen }">
                <span></span>
                <span></span>
                <span></span>
              </button>
            </nav>
          </div>
        </header>
        
        <div class="mobile-menu" :class="{ 'show': mobileMenuOpen }">
          <div class="mobile-menu-content">
            <a href="/" class="mobile-link" @click="handleInternalLink($event, '/'); closeMenu()">Home</a>
            <a href="/#about" class="mobile-link" @click="closeMenu()">Über uns</a>
            <a href="/#shows" class="mobile-link" @click="closeMenu()">Shows</a>
            <a href="/#music" class="mobile-link" @click="closeMenu()">Musik</a>
            <a href="/#photos" class="mobile-link" @click="closeMenu()">Fotos</a>
            <a href="/#videos" class="mobile-link" @click="closeMenu()">Videos</a>
            <a href="/#contact" class="mobile-link" @click="closeMenu()">Kontakt</a>
            <a v-if="shouldShowFanSection" href="/fan-galerie" class="mobile-link" @click="handleInternalLink($event, '/fan-galerie'); closeMenu()">{{ $t('header.fanZone') }}</a>
            
            <div class="mobile-social">
              <a v-if="siteSettings.facebook_url" :href="siteSettings.facebook_url" target="_blank" class="mobile-social-link">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                </svg>
              </a>
              
              <a v-if="siteSettings.youtube_url" :href="siteSettings.youtube_url" target="_blank" class="mobile-social-link">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                </svg>
              </a>
              
              <a v-if="siteSettings.tiktok_url" :href="siteSettings.tiktok_url" target="_blank" class="mobile-social-link">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
              
              <a v-if="siteSettings.instagram_url" :href="siteSettings.instagram_url" target="_blank" class="mobile-social-link">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
              </a>
            </div>
            
            <div class="mobile-lang">
              <LanguageSwitcher />
            </div>
          </div>
        </div>

        <main>
          <!-- Impressum und Datenschutz -->
          <div v-if="showImprint">
            <ImprintPage />
          </div>
          <div v-else-if="showPrivacy">
            <PrivacyPage />
          </div>

          <!-- Fan-Bereich-Komponenten -->
          <div v-else-if="showFanGallery">
            <FanPhotoGallery />
          </div>
          <div v-else-if="showFanUpload">
            <FanPhotoUpload />
          </div>
          <div v-else-if="showFanQrCode">
            <FanPhotoQRCode />
          </div>
        </main>

        <!-- Footer für alle Seiten -->
        <footer class="footer">
          <div class="celtic-footer-border"></div>
          <div class="container">
            <div class="footer-content">
              <div class="footer-logo">
                <div class="logo-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12,2C9.1,2,6.8,4.3,6.8,7.2c0,1.7,0.8,3.3,2.1,4.3c-1.3,1-2.1,2.5-2.1,4.3c0,1.7,0.8,3.3,2.1,4.3C7.6,21,6.8,22.5,6.8,24h2.4c0-1.3,1.1-2.4,2.4-2.4c1.3,0,2.4,1.1,2.4,2.4h2.4c0-1.5-0.8-3-2.1-3.9c1.3-1,2.1-2.5,2.1-4.3c0-1.7-0.8-3.3-2.1-4.3c1.3-1,2.1-2.5,2.1-4.3C16.8,4.3,14.9,2,12,2z" fill="currentColor"/>
                  </svg>
                </div>
                HomeWard
              </div>
              
              <div class="footer-links">
                <a href="/impressum" class="footer-link" @click="handleInternalLink($event, '/impressum')">Impressum</a>
                <a href="/datenschutz" class="footer-link" @click="handleInternalLink($event, '/datenschutz')">Datenschutz</a>
                <a href="#" class="footer-link" @click.prevent="openCookieSettings">Cookie-Einstellungen</a>
              </div>
              
              <div class="footer-copyright-section">
                <span class="copyright">{{ siteSettings.footer_text }}</span>
                <span class="separator">|</span>
                <a href="https://www.instagram.com/darkyzockt_ttv/" target="_blank" class="made-by-link">
                  Made by Darkyzockt
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" class="instagram-icon">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </footer>
      </template>

      <template v-else>
        <!-- Normale Website -->
        <div class="noise-overlay"></div>
        
        <!-- Admin Wartungsmodus Banner -->
        <div v-if="showAdminBanner" class="admin-maintenance-notice">
          <div class="notice-content">
            <span class="warning-icon">⚠️</span>
            <span>Wartungsmodus ist aktiviert. Die Website ist für normale Besucher nicht sichtbar.</span>
            <a href="/admin" class="admin-link-btn">Admin-Bereich</a>
          </div>
        </div>
        
        <header class="header" :class="{ 'with-notice': showAdminBanner }">
          <div class="container">
            <nav class="nav">
              <a href="/" class="logo" @click="handleInternalLink($event, '/')">
                <div class="logo-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12,2C9.1,2,6.8,4.3,6.8,7.2c0,1.7,0.8,3.3,2.1,4.3c-1.3,1-2.1,2.5-2.1,4.3c0,1.7,0.8,3.3,2.1,4.3C7.6,21,6.8,22.5,6.8,24h2.4c0-1.3,1.1-2.4,2.4-2.4c1.3,0,2.4,1.1,2.4,2.4h2.4c0-1.5-0.8-3-2.1-3.9c1.3-1,2.1-2.5,2.1-4.3c0-1.7-0.8-3.3-2.1-4.3c1.3-1,2.1-2.5,2.1-4.3C16.8,4.3,14.9,2,12,2z" fill="currentColor"/>
                  </svg>
                </div>
                HomeWard
              </a>
              
              <div class="nav-links">
                <a href="/" class="nav-link" @click="handleInternalLink($event, '/')">Home</a>
                <a href="/#about" class="nav-link">Über uns</a>
                <a href="/#shows" class="nav-link">Shows</a>
                <a href="/#music" class="nav-link">Musik</a>
                <a href="/#photos" class="nav-link">Fotos</a>
                <a href="/#videos" class="nav-link">Videos</a>
                <a href="/#contact" class="nav-link">Kontakt</a>
                <a v-if="shouldShowFanSection" href="/fan-galerie" class="nav-link" @click="handleInternalLink($event, '/fan-galerie')">{{ $t('header.fanZone') }}</a>
              </div>
              
              <div class="header-social-links">
                <a v-if="siteSettings.facebook_url" :href="siteSettings.facebook_url" target="_blank" class="header-social-link" @click="openSocialLink(siteSettings.facebook_url)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                  </svg>
                </a>
                
                <a v-if="siteSettings.youtube_url" :href="siteSettings.youtube_url" target="_blank" class="header-social-link" @click="openSocialLink(siteSettings.youtube_url)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                  </svg>
                </a>
                
                <a v-if="siteSettings.tiktok_url" :href="siteSettings.tiktok_url" target="_blank" class="header-social-link" @click="openSocialLink(siteSettings.tiktok_url)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
                
                <a v-if="siteSettings.instagram_url" :href="siteSettings.instagram_url" target="_blank" class="header-social-link" @click="openSocialLink(siteSettings.instagram_url)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                </a>
              </div>
              
              <button class="mobile-toggle" @click="toggleMobileMenu" :class="{ 'active': mobileMenuOpen }">
                <span></span>
                <span></span>
                <span></span>
              </button>
            </nav>
          </div>
        </header>
        
        <div class="mobile-menu" :class="{ 'show': mobileMenuOpen }">
          <div class="mobile-menu-content">
            <a href="/" class="mobile-link" @click="handleInternalLink($event, '/'); closeMenu()">Home</a>
            <a href="/#about" class="mobile-link" @click="closeMenu()">Über uns</a>
            <a href="/#shows" class="mobile-link" @click="closeMenu()">Shows</a>
            <a href="/#music" class="mobile-link" @click="closeMenu()">Musik</a>
            <a href="/#photos" class="mobile-link" @click="closeMenu()">Fotos</a>
            <a href="/#videos" class="mobile-link" @click="closeMenu()">Videos</a>
            <a href="/#contact" class="mobile-link" @click="closeMenu()">Kontakt</a>
            <a v-if="shouldShowFanSection" href="/fan-galerie" class="mobile-link" @click="handleInternalLink($event, '/fan-galerie'); closeMenu()">{{ $t('header.fanZone') }}</a>
            
            <div class="mobile-social">
              <a v-if="siteSettings.facebook_url" :href="siteSettings.facebook_url" target="_blank" class="mobile-social-link">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                </svg>
              </a>
              
              <a v-if="siteSettings.youtube_url" :href="siteSettings.youtube_url" target="_blank" class="mobile-social-link">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                </svg>
              </a>
              
              <a v-if="siteSettings.tiktok_url" :href="siteSettings.tiktok_url" target="_blank" class="mobile-social-link">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
              
              <a v-if="siteSettings.instagram_url" :href="siteSettings.instagram_url" target="_blank" class="mobile-social-link">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
              </a>
            </div>
            
            <div class="mobile-lang">
              <LanguageSwitcher />
            </div>
          </div>
        </div>

        <main>
          <!-- Hero Section -->
          <section class="hero" :style="{ backgroundImage: heroBackgroundImage }">
            <div class="container">
              <div class="hero-content">
                <div class="hero-title">
                  <span class="title-line">HomeWard</span>
                  <span class="title-subtitle">Irish Folk Band</span>
                </div>
                <div class="hero-text" v-html="aboutContent.band_description || 'Willkommen bei HomeWard, Ihrer irischen Folk-Band.'"></div>
                <div class="hero-cta">
                  <a href="/#shows" class="btn btn-primary">Shows</a>
                  <a href="/#contact" class="btn btn-outline">Kontakt</a>
                </div>
              </div>
            </div>
            <div class="scroll-prompt">
              <span>Scrollen</span>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 5v14M5 12l7 7 7-7"/>
              </svg>
            </div>
          </section>

          <!-- About Section -->
          <section id="about" class="about">
            <div class="container">
              <div class="section-header">
                <h2>Über uns</h2>
                <div class="celtic-divider">
                  <span></span>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M12 2C9.1 2 6.8 4.3 6.8 7.2c0 1.7.8 3.3 2.1 4.3-1.3 1-2.1 2.5-2.1 4.3c0 1.7.8 3.3 2.1 4.3C7.6 21 6.8 22.5 6.8 24h2.4c0-1.3 1.1-2.4 2.4-2.4c1.3,0,2.4,1.1,2.4,2.4h2.4c0-1.5-.8-3-2.1-3.9c1.3-1,2.1-2.5,2.1-4.3c0-1.7-.8-3.3-2.1-4.3c1.3-1,2.1-2.5,2.1-4.3C16.8,4.3,14.9,2,12,2z"/>
                  </svg>
                  <span></span>
                </div>
              </div>
              
              <div class="about-content">
                <div class="about-image">
                  <img :src="aboutContent.band_image || '/upload/homeward.jpg'" alt="HomeWard Band" class="main-img">
                  <div class="about-image-accent"></div>
                </div>
                
                <div class="about-text">
                  <p class="lead-text" v-html="aboutContent.band_description || 'Willkommen bei HomeWard, Ihrer irischen Folk-Band.'"></p>
                  
                  <div class="band-members">
                    <div v-for="member in aboutContent.members" :key="member.id" class="member">
                      <div class="member-info">
                        <h3>{{ member.name }}</h3>
                        <p>{{ member.role }}</p>
                        <p v-if="member.bio" class="member-bio">{{ member.bio }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- Shows Section -->
          <ShowsSection :shows="shows" :calendar_image="siteSettings.calendar_image" />

          <!-- Music Section -->
          <MusicSection />

          <!-- Photos Section -->
          <PhotosSection :siteSettings="siteSettings" />

          <!-- Videos Section -->
          <VideoGallery />

          <!-- Contact Section -->
          <section id="contact" class="contact">
            <div class="container">
              <div class="section-header">
                <h2>Kontakt</h2>
                <div class="celtic-divider">
                  <span></span>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M12 2C9.1 2 6.8 4.3 6.8 7.2c0 1.7.8 3.3 2.1 4.3-1.3 1-2.1 2.5-2.1 4.3c0 1.7.8 3.3 2.1 4.3C7.6 21 6.8 22.5 6.8 24h2.4c0-1.3 1.1-2.4 2.4-2.4c1.3 0 2.4 1.1 2.4 2.4h2.4c0-1.5-.8-3-2.1-3.9c1.3-1 2.1-2.5 2.1-4.3c0-1.7-.8-3.3-2.1-4.3 1.3-1 2.1-2.5 2.1-4.3C16.8 4.3 14.9 2 12 2z"/>
                  </svg>
                  <span></span>
                </div>
              </div>
              
              <div class="contact-content contact-centered">
                <div class="contact-card">
                  <!-- Telefon nur anzeigen, wenn vorhanden -->
                  <div class="info-item" v-if="siteSettings.contact_phone">
                    <div class="info-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                      </svg>
                    </div>
                    <div>
                      <h3>Telefon</h3>
                      <a :href="'tel:' + siteSettings.contact_phone">{{ siteSettings.contact_phone }}</a>
                    </div>
                  </div>
                  
                  <!-- E-Mail nur anzeigen, wenn vorhanden -->
                  <div class="info-item" v-if="siteSettings.contact_email">
                    <div class="info-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <path d="M22 6l-10 7L2 6"/>
                      </svg>
                    </div>
                    <div>
                      <h3>E-Mail</h3>
                      <a :href="'mailto:' + siteSettings.contact_email">{{ siteSettings.contact_email }}</a>
                    </div>
                  </div>
                  
                  <!-- Standort nur anzeigen, wenn vorhanden -->
                  <div class="info-item" v-if="siteSettings.contact_location">
                    <div class="info-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                        <circle cx="12" cy="10" r="3"/>
                      </svg>
                    </div>
                    <div>
                      <h3>Standort</h3>
                      <p>{{ siteSettings.contact_location }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <!-- Footer für alle Seiten -->
        <footer class="footer">
          <div class="celtic-footer-border"></div>
          <div class="container">
            <div class="footer-content">
              <div class="footer-logo">
                <div class="logo-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12,2C9.1,2,6.8,4.3,6.8,7.2c0,1.7,0.8,3.3,2.1,4.3c-1.3,1-2.1,2.5-2.1,4.3c0,1.7,0.8,3.3,2.1,4.3C7.6,21,6.8,22.5,6.8,24h2.4c0-1.3,1.1-2.4,2.4-2.4c1.3,0,2.4,1.1,2.4,2.4h2.4c0-1.5-0.8-3-2.1-3.9c1.3-1,2.1-2.5,2.1-4.3c0-1.7-0.8-3.3-2.1-4.3c1.3-1,2.1-2.5,2.1-4.3C16.8,4.3,14.9,2,12,2z" fill="currentColor"/>
                  </svg>
                </div>
                HomeWard
              </div>
              
              <div class="footer-links">
                <a href="/impressum" class="footer-link" @click="handleInternalLink($event, '/impressum')">Impressum</a>
                <a href="/datenschutz" class="footer-link" @click="handleInternalLink($event, '/datenschutz')">Datenschutz</a>
                <a href="#" class="footer-link" @click.prevent="openCookieSettings">Cookie-Einstellungen</a>
              </div>
              
              <div class="footer-copyright-section">
                <span class="copyright">{{ siteSettings.footer_text }}</span>
                <span class="separator">|</span>
                <a href="https://www.instagram.com/darkyzockt_ttv/" target="_blank" class="made-by-link">
                  Made by Darkyzockt
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" class="instagram-icon">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </footer>
      </template>
    </template>
    
    <!-- Cookie Banner, wird nicht im Admin-Bereich angezeigt -->
    <CookieBanner v-if="!isAdminRoute" ref="cookieBannerRef" />
    
    <!-- Scroll-to-Top Button, wird nicht im Admin-Bereich angezeigt -->
    <ScrollToTop v-if="!isAdminRoute" />
  </div>
</template>

<style scoped>
/* Grundlegende Stile */
.app {
  min-height: 100vh;
  background-color: var(--bg-color);
  position: relative;
  overflow-x: hidden;
  color: var(--text-color);
}

/* Globale Styles für Erfolgs- und Fehlermeldungen */
:deep(.success-message),
:deep(.error-message) {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 1rem 2rem;
  border-radius: 8px;
  font-weight: 500;
  z-index: 1000;
  animation: fadeInOut 3s ease-in-out;
  text-align: center;
  min-width: 300px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

:deep(.success-message) {
  background-color: #169b62;
  color: white;
  border: 1px solid #0f7a4d;
}

:deep(.error-message) {
  background-color: #dc3545;
  color: white;
  border: 1px solid #b02a37;
}

@keyframes fadeInOut {
  0% {
    opacity: 0;
    transform: translate(-50%, -40%);
  }
  15% {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
  85% {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -60%);
  }
}

.noise-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
  opacity: 0.03;
  z-index: 1;
  pointer-events: none;
}

.container {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 2;
}

/* Header & Navigation */
.header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 100;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.05);
}

.nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 0;
}

.logo {
  display: flex;
  align-items: center;
  font-family: 'Playfair Display', serif;
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--primary-color);
}

.logo-icon {
  width: 2rem;
  height: 2rem;
  margin-right: 0.75rem;
  color: var(--primary-color);
}

.nav-links {
  display: flex;
  gap: 3rem;
}

.nav-link {
  font-size: 1rem;
  font-weight: 500;
  color: var(--text-color);
  text-decoration: none;
  position: relative;
  padding: 0.5rem 0;
  transition: color 0.3s ease;
}

.nav-link::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 2px;
  background-color: var(--primary-color);
  transition: width 0.3s ease;
}

.nav-link:hover {
  color: var(--primary-color);
}

.nav-link:hover::after {
  width: 100%;
}

.header-social-links {
  display: flex;
  gap: 1rem;
  margin-left: 2rem;
}

.header-social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: rgba(0, 132, 61, 0.1);
  color: var(--primary-color);
  transition: all 0.3s ease;
}

.header-social-link:hover {
  background-color: var(--primary-color);
  color: white;
  transform: translateY(-3px);
}

.mobile-toggle {
  display: none;
  flex-direction: column;
  justify-content: space-between;
  width: 30px;
  height: 21px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 150;
}

.mobile-toggle span {
  display: block;
  width: 100%;
  height: 3px;
  background-color: var(--primary-color);
  border-radius: 3px;
  transition: all 0.3s ease;
}

/* Hero Section */
.hero {
  height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  padding: 6rem 0;
  overflow: hidden;
  margin-bottom: 3rem;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.hero-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  z-index: 0;
}

.hero-content {
  position: relative;
  z-index: 2;
  max-width: 900px;
  color: var(--white);
  
}

.hero-title {
  font-family: 'Playfair Display', serif;
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
}

.title-line {
  font-size: 5.5rem;
  font-weight: 700;
  line-height: 1.1;
  color: var(--white);
  margin-bottom: 1rem;
  letter-spacing: -1px;
}

.title-subtitle {
  font-size: 1.75rem;
  font-weight: 400;
  color: var(--accent-color);
}

.hero-text {
  font-size: 1.5rem;
  margin-bottom: 3rem;
  max-width: 600px;
  line-height: 1.5;
  color: var(--white);
}

.hero-text :deep(p) {
  margin: 0;
  padding: 0;
}

.hero-cta {
  display: flex;
  gap: 1.5rem;
}

.btn {
  display: inline-block;
  padding: 1rem 2.5rem;
  font-size: 1rem;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  border-radius: 50px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.btn-primary {
  background-color: var(--primary-color);
  color: var(--white);
  border: 2px solid var(--primary-color);
  box-shadow: 0 4px 20px rgba(0, 132, 61, 0.3);
}

.btn-outline {
  background-color: transparent;
  color: var(--white);
  border: 2px solid var(--white);
}

.btn-primary:hover {
  background-color: var(--primary-dark);
  border-color: var(--primary-dark);
  transform: translateY(-3px);
  box-shadow: 0 6px 25px rgba(0, 132, 61, 0.4);
}

.btn-outline:hover {
  background-color: var(--white);
  color: var(--text-color);
  transform: translateY(-3px);
}

.btn-small {
  padding: 0.5rem 1.25rem;
  font-size: 0.9rem;
  border-radius: 50px;
}

.scroll-prompt {
  position: absolute;
  bottom: 3rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  color: var(--white);
  font-size: 0.9rem;
  opacity: 0.8;
  animation: fadeInUp 2s infinite;
  z-index: 2;
}

.scroll-prompt span {
  margin-bottom: 0.5rem;
}

@keyframes fadeInUp {
  0% {
    opacity: 0;
    transform: translate(-50%, 10px);
  }
  50% {
    opacity: 0.8;
    transform: translate(-50%, 0);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -10px);
  }
}

/* Section Headers */
section {
  padding: 6rem 0;
}

.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.section-header h2 {
  font-family: 'Playfair Display', serif;
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: var(--primary-color);
}

.celtic-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  max-width: 300px;
}

.celtic-divider span {
  flex: 1;
  height: 1px;
  background-color: var(--accent-color);
  opacity: 0.5;
}

.celtic-divider svg {
  margin: 0 15px;
  color: var(--accent-color);
  opacity: 0.7;
}

/* About Section */
.about {
  background-color: var(--bg-color);
}

.about-content {
  display: grid;
  grid-template-columns: 40% 60%;
  gap: 4rem;
  align-items: center;
}

.about-image {
  position: relative;
  width: 100%;
  max-width: 500px;
}

.main-img {
  border-radius: 5px;
  width: 100%;
  height: auto;
  object-fit: cover;
  aspect-ratio: 4/5;
  position: relative;
  z-index: 2;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.1);
}

.about-image-accent {
  position: absolute;
  top: -10px;
  right: -10px;
  width: calc(100% + 20px);
  height: calc(100% + 20px);
  border: 3px solid transparent;
  border-radius: 8px;
  z-index: 1;
  background: linear-gradient(45deg, 
    #169b62, /* Irisches Grün */
    #ff883e, /* Irisches Orange */
    #169b62, /* Irisches Grün */
    #ff883e  /* Irisches Orange */
  );
  background-size: 300% 300%;
  animation: irishBorder 8s ease infinite;
}

@keyframes irishBorder {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.about-text {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.lead-text {
  font-size: 1.4rem;
  font-weight: 500;
  color: var(--primary-color);
  line-height: 1.5;
}

.about-text p {
  font-size: 1.1rem;
  line-height: 1.8;
}

.band-members {
  display: flex;
  gap: 2rem;
  margin-top: 2rem;
}

.member {
  flex: 1;
}

.member-info h3 {
  font-family: 'Playfair Display', serif;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--primary-color);
}

.member-info p {
  font-size: 1rem;
  color: var(--text-muted);
}

.member-bio {
  margin-top: 0.5rem;
  font-style: italic;
  font-size: 0.9rem;
  line-height: 1.4;
}

/* Music Section */
.music {
  background-color: var(--bg-alt);
  position: relative;
  overflow: hidden;
}

.music::before {
  content: '';
  position: absolute;
  top: -200px;
  right: -200px;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(0, 132, 61, 0.05) 0%, rgba(0, 132, 61, 0) 70%);
  border-radius: 50%;
}

.music-categories {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2.5rem;
  margin-bottom: 5rem;
}

.category-card {
  background-color: var(--white);
  padding: 3rem 2rem;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  text-align: center;
  transition: all 0.3s ease;
}

.category-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.1);
}

.card-icon {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  color: var(--primary-color);
  background-color: rgba(0, 132, 61, 0.1);
  border-radius: 50%;
}

.category-card h3 {
  font-family: 'Playfair Display', serif;
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: var(--primary-color);
}

.category-card p {
  font-size: 1rem;
  line-height: 1.6;
  color: var(--text-color);
}

.music-showcase {
  background-color: var(--white);
  padding: 3rem;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.showcase-intro h3 {
  font-family: 'Playfair Display', serif;
  font-size: 1.75rem;
  margin-bottom: 1rem;
  color: var(--primary-color);
}

.showcase-intro p {
  font-size: 1.1rem;
  margin-bottom: 2rem;
  color: var(--text-muted);
}

.streaming-links {
  display: flex;
  gap: 2.5rem;
  justify-content: center;
  flex-wrap: wrap;
}

.streaming-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--text-color);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
}

.streaming-link:hover {
  color: var(--primary-color);
}

/* Shows Section */
.shows {
  background-color: var(--bg-color);
}

.shows-content {
  display: grid;
  grid-template-columns: 40% 60%;
  gap: 4rem;
  margin-bottom: 2rem;
}

.shows-image {
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.calendar-img {
  max-width: 100%;
  height: auto;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.calendar-img:hover {
  transform: scale(1.02);
}

.shows-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.show-card {
  display: flex;
  align-items: center;
  gap: 2rem;
  background-color: var(--white);
  padding: 1.5rem 2rem;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.show-card:hover {
  transform: translateX(10px);
}

.show-date {
  text-align: center;
  min-width: 80px;
}

.show-date .day {
  display: block;
  font-size: 2rem;
  font-weight: 700;
  color: var(--primary-color);
  line-height: 1;
}

.show-date .month {
  display: block;
  font-size: 1rem;
  color: var(--text-muted);
  text-transform: uppercase;
}

.show-info {
  flex: 1;
}

.show-info h3 {
  font-size: 1.3rem;
  margin-bottom: 0.25rem;
  color: var(--text-color);
}

.show-info p {
  font-size: 0.9rem;
  color: var(--text-muted);
}

/* Contact Section */
.contact {
  background-color: var(--bg-alt);
  position: relative;
}

.contact-content {
  display: grid;
  grid-template-columns: 40% 60%;
  gap: 4rem;
}

.contact-centered {
  display: flex;
  justify-content: center;
  align-items: center;
  grid-template-columns: 1fr;
}

.contact-card {
  background-color: var(--white);
  border-radius: 12px;
  padding: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  transition: transform 0.3s ease;
  position: relative;
}

.contact-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
}

.contact-card::before {
  content: '';
  position: absolute;
  top: -4px;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--primary-color), var(--accent-color));
  border-radius: 4px 4px 0 0;
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: 3rem;
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
}

.info-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background-color: rgba(0, 132, 61, 0.1);
  border-radius: 50%;
  color: var(--primary-color);
}

.info-item h3 {
  font-family: 'Playfair Display', serif;
  font-size: 1.3rem;
  margin-bottom: 0.75rem;
  color: var(--primary-color);
}

.info-item a {
  color: var(--text-color);
  text-decoration: none;
  transition: color 0.3s ease;
}

.info-item a:hover {
  color: var(--primary-color);
}

.social-links {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  justify-content: center;
  margin-top: 1rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.social-link {
  color: var(--text-color);
  text-decoration: none;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 30px;
  background-color: rgba(0, 0, 0, 0.02);
}

.social-link:hover {
  color: var(--primary-color);
  background-color: rgba(0, 0, 0, 0.04);
  transform: translateY(-3px);
}

.social-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background-color: rgba(0, 132, 61, 0.1);
  border-radius: 50%;
  padding: 6px;
  color: var(--primary-color);
  transition: background-color 0.3s ease;
}

.social-link:hover .social-icon {
  background-color: rgba(0, 132, 61, 0.2);
}

.no-social-links {
  color: var(--text-muted);
  font-style: italic;
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .contact-card {
    padding: 2rem;
  }
  
  .info-item {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 1rem;
  }
  
  .social-links {
    flex-direction: column;
    align-items: center;
  }
}

/* Footer */
.footer {
  background-color: var(--primary-color);
  color: var(--white);
  padding: 3rem 0;
  position: relative;
}

.celtic-footer-border {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 10px;
  background-image: url("data:image/svg+xml,%3Csvg width='30' height='10' viewBox='0 0 30 10' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 5 L5 0 L10 5 L15 0 L20 5 L25 0 L30 5 L30 10 L0 10 Z' fill='%23f6b60c'/%3E%3C/svg%3E");
  background-repeat: repeat-x;
}

.footer-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.footer-logo {
  display: flex;
  align-items: center;
  font-family: 'Playfair Display', serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--white);
}

.footer-logo .logo-icon {
  color: var(--accent-color);
}

.footer-links {
  margin-bottom: 1rem;
}

.admin-link {
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  font-size: 0.9rem;
  transition: var(--transition-normal);
}

.admin-link:hover {
  color: var(--white);
}

.footer-link {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-size: 0.9rem;
  transition: var(--transition-normal);
  margin-right: 1.5rem;
}

.footer-link:last-child {
  margin-right: 0;
}

.footer-link:hover {
  color: var(--white);
  text-decoration: underline;
}

.footer-copyright-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  line-height: 1;
  margin-top: 0.5rem;
}

.copyright {
  font-size: 0.9rem;
  opacity: 0.8;
}

.separator {
  color: rgba(255, 255, 255, 0.5);
  margin: 0 0.25rem;
}

.made-by-link {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-size: 0.9rem;
  transition: var(--transition-normal);
  display: flex;
  align-items: center;
}

.made-by-link:hover {
  color: var(--white);
  text-decoration: underline;
}

.instagram-icon {
  width: 16px;
  height: 16px;
  margin-left: 0.5rem;
}

/* Responsive Styles */
@media (max-width: 1200px) {
  .music-categories {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  }
  
  .title-line {
    font-size: 4.5rem;
  }
}

@media (max-width: 992px) {
  .about-content,
  .contact-content,
  .shows-content {
    grid-template-columns: 1fr;
  }
  
  .about-image,
  .shows-image {
    max-width: 500px;
    margin: 0 auto 2rem;
  }
  
  .title-line {
    font-size: 4rem;
  }
  
  .header-social-links {
    margin-left: 1rem;
  }
}

@media (max-width: 768px) {
  .nav-links {
    display: none;
  }
  
  .mobile-toggle {
    display: flex;
  }
  
  .title-line {
    font-size: 3.5rem;
  }
  
  .title-subtitle {
    font-size: 1.5rem;
  }
  
  .hero-text {
    font-size: 1.25rem;
  }
  
  .hero-cta {
    flex-direction: column;
  }
  
  .section-header h2 {
    font-size: 2.5rem;
  }
  
  .contact-content {
    gap: 3rem;
  }
  
  .header-social-links {
    display: none;
  }
}

@media (max-width: 576px) {
  .container {
    padding: 0 1.5rem;
  }
  
  .title-line {
    font-size: 2.8rem;
  }
  
  .title-subtitle {
    font-size: 1.25rem;
  }
  
  .section-header h2 {
    font-size: 2rem;
  }
  
  .band-members {
    flex-direction: column;
    gap: 1.5rem;
  }
  
  .info-item {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
  
  .show-card {
    flex-direction: column;
    text-align: center;
    padding: 2rem;
    gap: 1rem;
  }
  
  .show-card .btn {
    margin-top: 1rem;
  }
  
  .footer-content {
    flex-direction: column;
    gap: 1.5rem;
    text-align: center;
  }
  
  .footer-copyright-section {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.5rem;
  }
  
  .separator {
    display: none;
  }
  
  .copyright, .made-by-link {
    margin: 0 0.25rem;
  }
}

/* Wartungsmodus Styles */
.maintenance-mode {
  min-height: 100vh;
  background-color: var(--bg-color);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.maintenance-container {
  max-width: 600px;
  text-align: center;
  padding: 3rem 2rem;
  position: relative;
  z-index: 1;
}

.maintenance-logo {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 2rem;
}

.maintenance-logo .logo-icon {
  width: 5rem;
  height: 5rem;
  color: var(--primary-color);
  margin-bottom: 1rem;
}

.maintenance-logo h1 {
  font-family: var(--font-family-serif);
  font-size: 2.5rem;
  color: var(--primary-color);
  margin: 0;
}

.maintenance-content {
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(5px);
  margin-bottom: 2rem;
}

.maintenance-content h2 {
  font-size: 1.75rem;
  margin-bottom: 1rem;
  color: var(--text-color);
}

.maintenance-content p {
  font-size: 1.1rem;
  line-height: 1.6;
  color: var(--text-muted);
  margin-bottom: 2rem;
}

.maintenance-tools {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 2rem;
}

.maintenance-tools .tool {
  width: 3.5rem;
  height: 3.5rem;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);
  animation: float 3s ease-in-out infinite alternate;
}

.maintenance-tools .tool:nth-child(2) {
  animation-delay: 0.5s;
}

@keyframes float {
  0% {
    transform: translateY(0) rotate(0);
  }
  100% {
    transform: translateY(-10px) rotate(10deg);
  }
}

.btn-admin {
  display: inline-block;
  padding: 0.65rem 1.5rem;
  background-color: var(--primary-color);
  color: #fff;
  text-decoration: none;
  border-radius: 4px;
  font-weight: 500;
  transition: var(--transition-normal);
}

.btn-admin:hover {
  background-color: var(--primary-color-dark);
}

.maintenance-footer {
  font-size: 0.9rem;
  color: var(--text-muted);
}

/* Admin Maintenance Banner */
.admin-maintenance-notice {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background-color: #fff3cd;
  color: #664d03;
  padding: 0.5rem 0;
  text-align: center;
  z-index: 1000;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid #ffecb5;
}

.notice-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0 1rem;
}

.warning-icon {
  font-size: 1.25rem;
}

.admin-link-btn {
  background-color: #664d03;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  margin-left: 0.5rem;
  text-decoration: none;
  font-weight: 500;
  transition: background-color 0.3s;
}

.admin-link-btn:hover {
  background-color: #513e02;
}

.header.with-notice {
  margin-top: 38px;
}

/* Neue Styles für die Shows-Sektion */
.month-group {
  margin-bottom: 1rem;
  border-radius: 8px;
  overflow: hidden;
  background-color: var(--white);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.month-header {
  width: 100%;
  padding: 1.25rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--white);
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
}

.month-header:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.month-header.active {
  background-color: var(--primary-color);
  color: white;
}

.month-name {
  font-size: 1.25rem;
  font-weight: 600;
  font-family: 'Playfair Display', serif;
}

.show-count {
  font-size: 0.9rem;
  opacity: 0.8;
}

.arrow-icon {
  transition: transform 0.3s ease;
}

.arrow-icon.rotated {
  transform: rotate(180deg);
}

.month-shows {
  padding: 1rem 2rem;
  background-color: var(--white);
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.show-card {
  display: flex;
  align-items: center;
  gap: 2rem;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.show-card:last-child {
  border-bottom: none;
}

.show-date {
  text-align: center;
  min-width: 80px;
}

.show-date .day {
  display: block;
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--primary-color);
  line-height: 1;
}

.show-date .month {
  display: block;
  font-size: 0.9rem;
  color: var(--text-muted);
  text-transform: uppercase;
}

.show-info {
  flex: 1;
}

.show-info h3 {
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
  color: var(--text-color);
}

.show-info p {
  font-size: 0.9rem;
  color: var(--text-muted);
}

@media (max-width: 768px) {
  .month-header {
    padding: 1rem;
  }
  
  .month-shows {
    padding: 0.5rem 1rem;
  }
  
  .show-card {
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;
    padding: 1rem 0;
  }
  
  .show-date {
    margin-bottom: 0.5rem;
  }
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  font-size: 1.2rem;
  color: #666;
}
</style>

<style>
/* Globale Variablen für Light/Dark Mode */
:root {
  /* Light theme (default) */
  --bg-color: #f8f9fa;
  --bg-alt: #f1f3f5;
  --white: #ffffff;
  --text-color: #333333;
  --text-muted: #6c757d;
  --primary-color: #00843d;
  --primary-dark: #006430;
  --danger-color: #e53935;
  --border-color: #e9ecef;
  --shadow-sm: 0 2px 4px rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px rgba(0, 0, 0, 0.1);
  --radius-sm: 4px;
  --radius-md: 8px;
  --transition-normal: all 0.3s ease;
}

/* Dark theme */
body.dark-mode {
  --bg-color: #212529;
  --bg-alt: #1a1d20;
  --white: #2a2e33;
  --text-color: #e9ecef;
  --text-muted: #adb5bd;
  --primary-color: #4db380;
  --primary-dark: #3d8e66;
  --danger-color: #f44336;
  --border-color: #343a40;
  --shadow-sm: 0 2px 4px rgba(0, 0, 0, 0.3);
  --shadow-md: 0 4px 6px rgba(0, 0, 0, 0.4);
}

body.dark-mode {
  background-color: var(--bg-color);
  color: var(--text-color);
}

/* Allgemeine Transitionen für Dark Mode */
body, button, a, div, section, aside, main, input, textarea, select, table, th, td {
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
}
</style>
